import React from 'react';
import PropTypes from 'prop-types';
import { t } from '../i18n';

const AUTONOMOUS = 'AUTONOMOUS';
const FIXED = 'FIXED';

function clamp(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(Math.max(0, Math.min(100, n)) * 10000) / 10000;
}

function defaultFixedMap(levels = [], source = {}) {
  const result = { 0: clamp(source?.['0']) };
  levels.forEach((level) => {
    result[String(level)] = clamp(source?.[String(level)]);
  });
  return result;
}

function FeeDialInput({ label, description, value, onChange, disabled = false, maxValue = 100 }) {
  const safeMax = clamp(maxValue);
  const safeValue = Math.min(clamp(value), safeMax);
  const [draft, setDraft] = React.useState(String(safeValue));

  React.useEffect(() => {
    setDraft(String(safeValue));
  }, [safeValue]);

  const commit = (raw) => {
    const next = Math.min(clamp(raw), safeMax);
    setDraft(String(next));
    onChange(next);
  };

  return (
    <div className={`fee-default-dial-card ${disabled ? 'is-disabled' : ''}`}>
      <div className="fee-default-dial-copy">
        <strong>{label}</strong>
        {description && <span>{description}</span>}
      </div>

      <div className="fee-default-dial-control">
        <div
          className="fee-default-dial"
          style={{ '--fee-dial-angle': `${safeValue * 3.6}deg` }}
          aria-hidden="true"
        >
          <div className="fee-default-dial-center">
            <b>{safeValue.toFixed(2)}</b>
            <span>%</span>
          </div>
        </div>

        <div className="fee-default-input-stack">
          <label>
            <span>{t('직접 입력')}</span>
            <span className="fee-default-number-input">
              <input
                type="number"
                min="0"
                max={safeMax}
                step="0.0001"
                inputMode="decimal"
                value={draft}
                disabled={disabled}
                onChange={(event) => {
                  const raw = event.target.value;
                  setDraft(raw);
                  if (raw !== '' && Number.isFinite(Number(raw))) onChange(Math.min(clamp(raw), safeMax));
                }}
                onBlur={() => commit(draft)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commit(draft);
                }}
              />
              <i>%</i>
            </span>
          </label>

          <input
            className="fee-default-range"
            type="range"
            min="0"
            max={safeMax}
            step="0.0001"
            value={safeValue}
            disabled={disabled}
            onChange={(event) => commit(event.target.value)}
            aria-label={label}
          />
        </div>
      </div>
    </div>
  );
}

FeeDialInput.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  maxValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default function AgentFeeDefaultModal({ open, status, onClose, onSaved }) {
  const ownerLevel = Number(status?.owner_level || 0);
  const lowerLevels = Array.isArray(status?.lower_agent_levels)
    ? status.lower_agent_levels.map(Number)
    : [];
  const initial = status?.own_policy || status?.policy || {};
  const ownerPool = clamp(status?.owner_branch_pool_percent ?? status?.owner_fee_percent ?? 0);

  const [mode, setMode] = React.useState(initial?.mode === FIXED ? FIXED : AUTONOMOUS);
  const [childAgentFee, setChildAgentFee] = React.useState(clamp(initial?.child_agent_fee_percent));
  const [fixedFees, setFixedFees] = React.useState(() => defaultFixedMap(lowerLevels, initial?.fixed_fee_by_level));
  const [directUserFee, setDirectUserFee] = React.useState(clamp(initial?.direct_user_fee_percent));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    const source = status?.own_policy || status?.policy || {};
    setMode(source?.mode === FIXED ? FIXED : AUTONOMOUS);
    setChildAgentFee(clamp(source?.child_agent_fee_percent));
    setFixedFees(defaultFixedMap(lowerLevels, source?.fixed_fee_by_level));
    setDirectUserFee(clamp(source?.direct_user_fee_percent));
    setError('');
  }, [open, status, lowerLevels.join(',')]);

  React.useEffect(() => {
    if (mode === FIXED) {
      setDirectUserFee(clamp(fixedFees['0']));
    }
  }, [mode, fixedFees]);

  React.useEffect(() => {
    if (!open) return undefined;
    const keydown = (event) => {
      if (event.key === 'Escape' && !saving) onClose?.();
    };
    document.addEventListener('keydown', keydown);
    return () => document.removeEventListener('keydown', keydown);
  }, [open, saving, onClose]);

  if (!open) return null;

  // v5: FIXED 값도 각 단계의 바로 상위 실제 Pool 안에서 적용되는 상대 배분율이다.
  const normalizedFixedFees = { ...fixedFees };
  lowerLevels.forEach((level) => {
    normalizedFixedFees[String(level)] = clamp(normalizedFixedFees[String(level)]);
  });
  normalizedFixedFees['0'] = clamp(normalizedFixedFees['0']);

  let fixedProjectedPool = ownerPool;
  const fixedProjectedByLevel = {};
  lowerLevels.forEach((level) => {
    fixedProjectedPool = Math.round((fixedProjectedPool * normalizedFixedFees[String(level)]) * 100) / 10000;
    fixedProjectedByLevel[String(level)] = fixedProjectedPool;
  });
  const fixedPreviousPool = fixedProjectedPool;
  const fixedProjectedUserPool = Math.round((fixedPreviousPool * normalizedFixedFees['0']) * 100) / 10000;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const policy = {
        configured: true,
        mode,
        child_agent_fee_percent: clamp(childAgentFee),
        fixed_fee_by_level: mode === FIXED ? normalizedFixedFees : fixedFees,
        direct_user_fee_percent: mode === FIXED
          ? normalizedFixedFees['0']
          : clamp(directUserFee),
      };
      await onSaved(policy);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError?.message || t('수수료 기본값 저장에 실패했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fee-default-modal-root" role="dialog" aria-modal="true" aria-labelledby="fee-default-title">
      <button className="fee-default-modal-backdrop" type="button" onClick={() => !saving && onClose?.()} aria-label={t('닫기')} />

      <section className="fee-default-modal-panel">
        <header className="fee-default-modal-header">
          <div>
            <p>AGENT FEE DEFAULT</p>
            <h2 id="fee-default-title">{t('에이전트 수수료 기본값 설정')}</h2>
            <span>{t('기본값은 신규 가입/신규 하위 생성 시 한 번만 적용되는 템플릿입니다. 기존 계정과 개별 설정 경로는 변경하지 않습니다.')}</span>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label={t('닫기')}>×</button>
        </header>

        <div className="fee-default-owner-strip">
          <div>
            <span>{t('현재 계정')}</span>
            <strong>lv{ownerLevel} · {status?.owner?.agent_id || '-'}</strong>
          </div>
          <div>
            <span>{t('현재 계정 브랜치 풀')}</span>
            <strong>{ownerPool.toFixed(2)}%</strong>
          </div>
          <div>
            <span>{t('기본값 성격')}</span>
            <strong>{t('신규 계정용 템플릿')}</strong>
          </div>
          <div>
            <span>{t('설정 상태')}</span>
            <strong>{status?.needs_configuration ? t('미설정') : t('설정됨')}</strong>
          </div>
        </div>

        <div className="fee-default-modal-body">
          <section className="fee-default-mode-section">
            <div className="fee-default-section-title">
              <span>01</span>
              <div>
                <h3>{t('하위 에이전트 설정 방식')}</h3>
                <p>{t('자율형과 고정 설정 중 하나를 선택합니다.')}</p>
              </div>
            </div>

            <div className="fee-default-mode-grid">
              <button
                type="button"
                className={`fee-default-mode-card ${mode === AUTONOMOUS ? 'is-selected' : ''}`}
                onClick={() => setMode(AUTONOMOUS)}
              >
                <span className="fee-default-radio" />
                <strong>{t('자율형')}</strong>
                <p>{t('신규 직속 하위에게 현재 계정 Pool의 몇 %를 내려줄지 정합니다. 생성 시 한 번만 적용되며 하위 계정에 자율형 기본값이 복사되지 않습니다.')}</p>
                <small>lv{ownerLevel} → {lowerLevels[0] ? `lv${lowerLevels[0]}` : t('직속 유저')}</small>
              </button>

              <button
                type="button"
                className={`fee-default-mode-card ${mode === FIXED ? 'is-selected' : ''}`}
                onClick={() => setMode(FIXED)}
              >
                <span className="fee-default-radio" />
                <strong>{t('고정 설정')}</strong>
                <p>{t('신규 계정 생성 시 각 레벨에서 바로 상위 Pool의 몇 %를 내려줄지 고정 템플릿으로 사용합니다. 기존 계정이나 개별 수정 경로를 변경하거나 잠그지 않습니다.')}</p>
                <small>{[`lv${ownerLevel}`, ...lowerLevels.map((level) => `lv${level}`), t('유저')].join(' → ')}</small>
              </button>
            </div>
          </section>

          {mode === AUTONOMOUS ? (
            <section className="fee-default-config-section">
              <div className="fee-default-section-title">
                <span>02</span>
                <div>
                  <h3>{t('자율형 기본값')}</h3>
                  <p>{t('신규 직속 하위 에이전트에게 현재 Pool의 몇 %를 내려줄지 정하는 템플릿입니다.')}</p>
                </div>
              </div>

              {lowerLevels.length ? (
                <FeeDialInput
                  label={t('lv{from} → lv{to} 기본 브랜치 풀', { from: ownerLevel, to: lowerLevels[0] })}
                  description={t('예: 현재 Pool이 50%이고 기본값이 50%면 신규 직속 하위의 실제 Pool은 25%가 됩니다. 이미 존재하는 계정에는 적용되지 않습니다.')}
                  value={childAgentFee}
                  onChange={setChildAgentFee}
                  maxValue={100}
                />
              ) : (
                <div className="fee-default-empty-note">{t('lv4는 추가 하위 에이전트가 없어 유저 가입 기본값만 설정합니다.')}</div>
              )}
            </section>
          ) : (
            <section className="fee-default-config-section">
              <div className="fee-default-section-title">
                <span>02</span>
                <div>
                  <h3>{t('전체 고정 기본값')}</h3>
                  <p>{t('신규 생성될 각 레벨에서 바로 상위 Pool의 몇 %를 내려줄지 정합니다. 각 단계는 0~100% 범위입니다.')}</p>
                </div>
              </div>

              <div className="fee-default-fixed-grid">
                {lowerLevels.map((level) => {
                  const projectedPool = fixedProjectedByLevel[String(level)] || 0;
                  return (
                    <FeeDialInput
                      key={level}
                      label={t('lv{level} 상위 Pool 기준 배분율', { level })}
                      description={t('해당 레벨 신규 생성 시 바로 상위 실제 Pool에 이 비율을 곱합니다. 예상 실제 Pool: {pool}%', { level, pool: projectedPool.toFixed(4) })}
                      value={normalizedFixedFees[String(level)] || 0}
                      onChange={(next) => setFixedFees((previous) => ({ ...previous, [String(level)]: next }))}
                      maxValue={100}
                    />
                  );
                })}
                <FeeDialInput
                  label={t('유저 상위 Pool 기준 배분율')}
                  description={t('신규 유저 생성 시 직속 상위의 실제 Pool에 이 비율을 곱합니다.')}
                  value={normalizedFixedFees['0'] || 0}
                  onChange={(next) => setFixedFees((previous) => ({ ...previous, 0: next }))}
                  maxValue={100}
                />
              </div>

              <div className="fee-default-total-line">
                <span>{t('현재 풀 기준 최종 유저 예상 풀')}</span>
                <strong>{fixedProjectedUserPool.toFixed(4)}%</strong>
              </div>
            </section>
          )}

          <section className="fee-default-config-section is-user-signup">
            <div className="fee-default-section-title">
              <span>03</span>
              <div>
                <h3>{t('유저 회원가입 타입')}</h3>
                <p>{t('유저가 현재 에이전트의 추천 코드를 통해 직접 가입할 때 적용됩니다.')}</p>
              </div>
            </div>

            <FeeDialInput
              label={t('추천인 가입 유저 기본 수수료')}
              description={mode === FIXED
                ? t('고정형 유저 배분율과 동일하게 유지됩니다.')
                : t('현재 계정 Pool 안에서 신규 유저에게 내려줄 비율로 한 번만 적용됩니다.', { level: ownerLevel })}
              value={mode === FIXED ? normalizedFixedFees['0'] || 0 : directUserFee}
              onChange={setDirectUserFee}
              maxValue={100}
              disabled={mode === FIXED}
            />
          </section>

          <section className="fee-default-preview">
            <div>
              <span>{t('적용 방식')}</span>
              <strong>{mode === FIXED ? t('고정 설정') : t('자율형')}</strong>
            </div>
            <div>
              <span>{t('직접 가입 유저 배분율')}</span>
              <strong>{(mode === FIXED ? normalizedFixedFees['0'] : clamp(directUserFee)).toFixed(2)}%</strong>
            </div>
            <p>{t('저장 후 생성되는 계정에만 적용됩니다. 이미 존재하는 계정의 수수료나 개별 설정값은 변경/잠금되지 않습니다.')}</p>
          </section>

          {error && <div className="fee-default-error" role="alert">{error}</div>}
        </div>

        <footer className="fee-default-modal-footer">
          <button type="button" onClick={onClose} disabled={saving}>{t('나중에 설정')}</button>
          <button type="button" className="is-primary" onClick={save} disabled={saving}>
            {saving ? t('저장 중...') : t('기본값 저장')}
          </button>
        </footer>
      </section>
    </div>
  );
}

AgentFeeDefaultModal.propTypes = {
  open: PropTypes.bool,
  status: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
